import React from 'react';

import { formatEntryDateTime } from '~/components/ClaimPage/CliveApp/ConductorHub/utils';
import type { NumberOfLines } from '~/components/core/Molecules/Collapsible/ShowAllCollapsible';
import Text from '~/components/core/TextComponents/Text';
import cn from '~/Utils/cn';

import NotificationMessage from './NotificationMessage';
import type { StpNotification } from './types';

interface NarrationBodyProps {
  notification: StpNotification;
  /** Clamp the message to this many lines behind Show more. */
  visibleLines?: NumberOfLines;
}

/**
 * The body of a plain entry: the title and time on one line, the message under them, and the
 * entry's author where the stream named one.
 *
 * No frame and no card. There are eighty of these on a busy claim and nobody has to act on any of
 * them — giving narration edges is how a log stops being skimmable.
 */
const NarrationBody: React.FC<NarrationBodyProps> = ({ notification, visibleLines }) => {
  const { title, message, metadata, timestamp } = notification;
  const time = formatEntryDateTime(timestamp);
  const author = typeof metadata?.author === 'string' ? metadata.author : undefined;

  return (
    <>
      <div className="flex flex-wrap items-center gap-8">
        {title ? (
          <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.SEMI_BOLD}>
            {title}
          </Text>
        ) : null}
        {time ? (
          <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.SECONDARY} className="whitespace-nowrap">
            {time}
          </Text>
        ) : null}
      </div>
      {message ? (
        <NotificationMessage message={message} className={cn(title ? 'mt-2' : undefined)} visibleLines={visibleLines} />
      ) : null}
      {author ? (
        <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.SECONDARY} className="mt-2">
          {author}
        </Text>
      ) : null}
    </>
  );
};

export default NarrationBody;
